import { useState, type FormEvent } from 'react'
import { useCreateService, useUpdateService } from '../hooks/useServices'
import {
  ModalShell, Field, inputClass, ServerError,
  CancelButton, PrimaryButton, DangerButton
} from './modal-form'
import type { Service, ServiceCreate } from '../types'

// ServiceEditor - one modal for both create and edit (`service` null = new).
// Mirrors ComponentEditor / ActivityEditor in shape. No financial gating
// (services have no cost field) and no attachments in v1.
//
// Time validation: HH:MM 24-hour, zero-padded. Mirrors CK_Services_TimeText
// at the SQL boundary and the server-side TIME_RE.

const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/

interface Props {
  eventId: number
  service: Service | null   // null = creating new
  onClose: () => void
  onDelete?: () => void
  deleting?: boolean
}

export function ServiceEditor({
  eventId, service, onClose, onDelete, deleting
}: Props) {
  const isEditing = service !== null

  const createMut = useCreateService(eventId)
  const updateMut = useUpdateService(eventId)
  const mut = isEditing ? updateMut : createMut

  const [serviceLabel, setServiceLabel] = useState(service?.serviceLabel ?? '')
  const [timeText, setTimeText] = useState(service?.timeText ?? '')
  const [loco, setLoco] = useState(service?.loco ?? '')
  // Default origin/destination on create match the prototype (Pickering →
  // Newbridge return). On edit we use the stored values verbatim.
  const [originStation, setOriginStation] = useState(
    service?.originStation ?? (isEditing ? '' : 'Pickering')
  )
  const [destinationStation, setDestinationStation] = useState(
    service?.destinationStation ?? (isEditing ? '' : 'Newbridge')
  )
  const [capacity, setCapacity] = useState(
    service?.capacity != null ? String(service.capacity) : ''
  )
  const [notes, setNotes] = useState(service?.notes ?? '')

  const trimmedLabel = serviceLabel.trim()
  const trimmedTime = timeText.trim()
  const capacityNum = capacity === '' ? null : Number(capacity)

  const fieldErrors: { serviceLabel?: string; timeText?: string; capacity?: string; loco?: string; originStation?: string; destinationStation?: string } = {}

  if (trimmedLabel.length === 0) fieldErrors.serviceLabel = 'Service label is required'
  else if (trimmedLabel.length > 60) fieldErrors.serviceLabel = 'Must be 60 characters or fewer'

  if (trimmedTime.length === 0) fieldErrors.timeText = 'Time is required'
  else if (!TIME_RE.test(trimmedTime)) fieldErrors.timeText = 'Use HH:MM in 24-hour format (e.g. 09:30, 14:55)'

  if (loco.length > 120) fieldErrors.loco = 'Must be 120 characters or fewer'
  if (originStation.length > 80) fieldErrors.originStation = 'Must be 80 characters or fewer'
  if (destinationStation.length > 80) fieldErrors.destinationStation = 'Must be 80 characters or fewer'

  if (
    capacity !== '' &&
    (capacityNum === null || !Number.isInteger(capacityNum) || capacityNum < 0)
  ) {
    fieldErrors.capacity = 'Capacity must be a whole number (or leave blank)'
  }

  const isValid = Object.keys(fieldErrors).length === 0
  const saving = mut.isPending

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: ServiceCreate = {
      serviceLabel: trimmedLabel,
      timeText: trimmedTime,
      loco: loco.trim() === '' ? null : loco.trim(),
      originStation: originStation.trim() === '' ? null : originStation.trim(),
      destinationStation: destinationStation.trim() === '' ? null : destinationStation.trim(),
      capacity: capacityNum,
      notes: notes.trim() === '' ? null : notes,
      sortOrder: service?.sortOrder ?? 0
    }
    if (isEditing && service) {
      updateMut.mutate({ id: service.id, body }, { onSuccess: () => onClose() })
    } else {
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit service' : 'Add service'}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          {onDelete && (
            <DangerButton onClick={onDelete} disabled={saving || deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving || deleting} />
          <PrimaryButton disabled={!isValid || saving || deleting}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add service'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Service" error={fieldErrors.serviceLabel}>
              <input
                type="text"
                value={serviceLabel}
                onChange={(e) => setServiceLabel(e.target.value)}
                maxLength={60}
                autoFocus
                placeholder="Service 1"
                className={inputClass(!!fieldErrors.serviceLabel)}
              />
            </Field>
            <Field label="Time" hint="HH:MM (24h)" error={fieldErrors.timeText}>
              <input
                type="text"
                inputMode="numeric"
                value={timeText}
                onChange={(e) => setTimeText(e.target.value)}
                maxLength={5}
                placeholder="09:30"
                className={`${inputClass(!!fieldErrors.timeText)} font-mono`}
              />
            </Field>
            <Field label="Capacity" error={fieldErrors.capacity}>
              <input
                type="number"
                min="0"
                step="1"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="—"
                className={inputClass(!!fieldErrors.capacity)}
              />
            </Field>
          </div>

          <Field label="Locomotive" error={fieldErrors.loco}>
            <input
              type="text"
              value={loco}
              onChange={(e) => setLoco(e.target.value)}
              maxLength={120}
              placeholder="e.g. Thomas (No.1), Repton 926, Class 25"
              className={inputClass(!!fieldErrors.loco)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="From" error={fieldErrors.originStation}>
              <input
                type="text"
                value={originStation}
                onChange={(e) => setOriginStation(e.target.value)}
                maxLength={80}
                placeholder="Pickering"
                className={inputClass(!!fieldErrors.originStation)}
              />
            </Field>
            <Field label="To" error={fieldErrors.destinationStation}>
              <input
                type="text"
                value={destinationStation}
                onChange={(e) => setDestinationStation(e.target.value)}
                maxLength={80}
                placeholder="Newbridge"
                className={inputClass(!!fieldErrors.destinationStation)}
              />
            </Field>
          </div>

          <Field label="Notes" hint="Internal">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className={`${inputClass(false)} resize-y font-body`}
              placeholder="Optional - special stops, photo opportunities, exceptions."
            />
          </Field>

          {mut.isError && (
            <ServerError
              title={`Could not ${isEditing ? 'save' : 'add'} service`}
              error={mut.error as Error}
            />
          )}
        </div>
      }
    />
  )
}
